import {
  parseTieBreakerMode,
  rankEntriesWithTieBreakerMode,
  type TieBreakerCategoryInput,
  type TieBreakerMode,
} from "./tie-breakers";

export const LIVE_ACTIVITY_TOP_RANK = 3;
export const LIVE_ACTIVITY_TOP_PLAYER_LIMIT = 5;

export type LiveLeadChange = "opened" | "changed" | "unchanged";

export interface LiveActivityPlayer {
  userId: string;
  name: string;
  rank: number;
  previousRank: number | null;
  score: number;
}

export interface LiveShowActivity {
  id: string;
  at: string;
  categoryId: string;
  categoryName: string;
  winnerName: string;
  winnerImageUrl: string | null;
  currentUserCorrect: boolean | null;
  correctCount: number;
  correctNames: string[];
  announcedCount: number;
  leaders: LiveActivityPlayer[];
  currentUser: LiveActivityPlayer | null;
  leadChange: LiveLeadChange;
  top: LiveActivityPlayer[];
}

export interface LiveActivityParticipantInput {
  userId: string;
  name: string;
}

export interface LiveActivityCategoryInput {
  id: string;
  name: string;
  orderIndex: number;
  winnerName: string;
  winnerImageUrl: string | null;
  winnerNomineeIds: readonly string[];
  announcedAt: string;
}

export interface BuildLiveActivitiesInput {
  currentUserId: string;
  tieBreakerMode: TieBreakerMode;
  participants: readonly LiveActivityParticipantInput[];
  categories: readonly LiveActivityCategoryInput[];
  votesByUserId: Record<string, Record<string, string>>;
}

export interface GetLiveActivitiesResponse {
  activities: LiveShowActivity[];
}

export interface LiveActivitySourceResult {
  announcedAt: string | Date;
  nomineeId: string;
  nomineeName: string;
  nomineeImageUrl: string | null;
}

export interface LiveActivitySourceCategory {
  id: string;
  name: string;
  orderIndex: number;
  results: readonly LiveActivitySourceResult[];
}

export interface LiveActivitySourceParticipant {
  userId: string;
  username?: string | null;
  name?: string | null;
  email?: string | null;
}

export interface LiveActivitySourceVote {
  userId: string;
  categoryId: string;
  nomineeId: string;
}

function announcedTimeMs(value: string): number {
  const time = Date.parse(value);
  return Number.isFinite(time) ? time : Number.NaN;
}

function sameIdSet(left: readonly string[], right: readonly string[]): boolean {
  if (left.length !== right.length) {
    return false;
  }
  const sortedLeft = [...left].sort();
  const sortedRight = [...right].sort();
  return sortedLeft.every((id, index) => id === sortedRight[index]);
}

function displayName(participant: LiveActivitySourceParticipant): string {
  return (
    participant.username?.trim() ||
    participant.name?.trim() ||
    participant.email?.trim() ||
    participant.userId
  );
}

function dedupeParticipants(
  participants: readonly LiveActivityParticipantInput[],
): LiveActivityParticipantInput[] {
  const seen = new Set<string>();
  const unique: LiveActivityParticipantInput[] = [];
  for (const participant of participants) {
    const userId = participant.userId.trim();
    if (!userId || seen.has(userId)) {
      continue;
    }
    seen.add(userId);
    unique.push({
      userId,
      name: participant.name.trim() || userId,
    });
  }
  return unique;
}

function announcedCategories(
  categories: readonly LiveActivityCategoryInput[],
): LiveActivityCategoryInput[] {
  const sorted = [...categories]
    .filter(
      (category) =>
        category.winnerNomineeIds.length > 0 &&
        Number.isFinite(announcedTimeMs(category.announcedAt)),
    )
    .sort((left, right) => {
      const timeDiff =
        announcedTimeMs(left.announcedAt) - announcedTimeMs(right.announcedAt);
      if (timeDiff !== 0) {
        return timeDiff;
      }
      if (left.orderIndex !== right.orderIndex) {
        return left.orderIndex - right.orderIndex;
      }
      return left.id.localeCompare(right.id);
    });

  const seen = new Set<string>();
  const unique: LiveActivityCategoryInput[] = [];
  for (const category of sorted) {
    if (seen.has(category.id)) {
      continue;
    }
    seen.add(category.id);
    unique.push(category);
  }
  return unique;
}

function votedNomineeId(
  votesByUserId: Record<string, Record<string, string>>,
  userId: string,
  categoryId: string,
): string | null {
  const nomineeId = votesByUserId[userId]?.[categoryId]?.trim();
  return nomineeId ? nomineeId : null;
}

function toPlayer(
  entry: { userId: string; rank: number; correctCount: number },
  names: ReadonlyMap<string, string>,
  previousRanks: ReadonlyMap<string, number>,
): LiveActivityPlayer {
  return {
    userId: entry.userId,
    name: names.get(entry.userId) ?? entry.userId,
    rank: entry.rank,
    previousRank: previousRanks.has(entry.userId)
      ? (previousRanks.get(entry.userId) ?? null)
      : null,
    score: entry.correctCount,
  };
}

export function normalizeLiveActivitySource(input: {
  currentUserId: string;
  tieBreakerMode: unknown;
  participants: readonly LiveActivitySourceParticipant[];
  categories: readonly LiveActivitySourceCategory[];
  votes: readonly LiveActivitySourceVote[];
}): BuildLiveActivitiesInput {
  const votesByUserId: Record<string, Record<string, string>> = {};
  for (const vote of input.votes) {
    const byCategory = votesByUserId[vote.userId] ?? {};
    if (!byCategory[vote.categoryId] && vote.nomineeId.trim()) {
      byCategory[vote.categoryId] = vote.nomineeId;
    }
    votesByUserId[vote.userId] = byCategory;
  }

  const categories: LiveActivityCategoryInput[] = [];
  for (const category of input.categories) {
    const timed = category.results.flatMap((result) => {
      const raw =
        result.announcedAt instanceof Date
          ? result.announcedAt.toISOString()
          : result.announcedAt;
      const time = Date.parse(raw);
      return Number.isFinite(time) ? [{ result, time }] : [];
    });
    if (timed.length === 0) {
      continue;
    }

    const announcedAt = new Date(
      Math.max(...timed.map((item) => item.time)),
    ).toISOString();
    const winnerNames = timed
      .map((item) => item.result.nomineeName.trim())
      .filter((name) => name.length > 0);

    categories.push({
      id: category.id,
      name: category.name,
      orderIndex: category.orderIndex,
      winnerName: winnerNames.join(" / "),
      winnerImageUrl:
        timed.length === 1 ? (timed[0]?.result.nomineeImageUrl ?? null) : null,
      winnerNomineeIds: timed.map((item) => item.result.nomineeId),
      announcedAt,
    });
  }

  return {
    currentUserId: input.currentUserId,
    tieBreakerMode: parseTieBreakerMode(input.tieBreakerMode),
    participants: input.participants.map((participant) => ({
      userId: participant.userId,
      name: displayName(participant),
    })),
    categories,
    votesByUserId,
  };
}

export function buildLiveActivities(
  input: BuildLiveActivitiesInput,
): LiveShowActivity[] {
  const participants = dedupeParticipants(input.participants);
  if (participants.length === 0) {
    return [];
  }

  const categories = announcedCategories(input.categories);
  if (categories.length === 0) {
    return [];
  }

  const names = new Map(
    participants.map((participant) => [participant.userId, participant.name]),
  );
  const participantIds = participants.map((participant) => participant.userId);
  const activities: LiveShowActivity[] = [];
  let previousRanks = new Map<string, number>();
  let previousLeaderIds: string[] = [];

  for (const [index, category] of categories.entries()) {
    const prefix = categories.slice(0, index + 1);
    const scores = new Map<string, number>();
    for (const participant of participants) {
      let score = 0;
      for (const announced of prefix) {
        const nomineeId = votedNomineeId(
          input.votesByUserId,
          participant.userId,
          announced.id,
        );
        if (nomineeId && announced.winnerNomineeIds.includes(nomineeId)) {
          score += 1;
        }
      }
      scores.set(participant.userId, score);
    }

    const tieBreakerCategories: TieBreakerCategoryInput[] = prefix.map(
      (announced) => ({
        id: announced.id,
        orderIndex: announced.orderIndex,
        resultNomineeIds: announced.winnerNomineeIds,
        announcedAt: announced.announcedAt,
      }),
    );
    const ranked = rankEntriesWithTieBreakerMode(
      participantIds.map((userId) => ({
        userId,
        correctCount: scores.get(userId) ?? 0,
      })),
      {
        mode: input.tieBreakerMode,
        categories: tieBreakerCategories,
        votesByUserId: input.votesByUserId,
        participantUserIds: participantIds,
      },
    );

    const leaders = ranked
      .filter((entry) => entry.rank === 1 && entry.correctCount > 0)
      .map((entry) => toPlayer(entry, names, previousRanks));
    const leaderIds = leaders.map((leader) => leader.userId);
    let leadChange: LiveLeadChange = "unchanged";
    if (leaderIds.length > 0) {
      if (previousLeaderIds.length === 0) {
        leadChange = "opened";
      } else if (!sameIdSet(previousLeaderIds, leaderIds)) {
        leadChange = "changed";
      }
    }

    const top: LiveActivityPlayer[] = [];
    for (const entry of ranked) {
      if (
        top.length >= LIVE_ACTIVITY_TOP_PLAYER_LIMIT ||
        entry.rank > LIVE_ACTIVITY_TOP_RANK
      ) {
        break;
      }
      top.push(toPlayer(entry, names, previousRanks));
    }

    const currentEntry =
      ranked.find((entry) => entry.userId === input.currentUserId) ?? null;
    const currentVote = votedNomineeId(
      input.votesByUserId,
      input.currentUserId,
      category.id,
    );
    const correctParticipants = participants.filter((participant) => {
      const nomineeId = votedNomineeId(
        input.votesByUserId,
        participant.userId,
        category.id,
      );
      return Boolean(
        nomineeId && category.winnerNomineeIds.includes(nomineeId),
      );
    });

    activities.push({
      id: `activity:${category.id}`,
      at: new Date(announcedTimeMs(category.announcedAt)).toISOString(),
      categoryId: category.id,
      categoryName: category.name,
      winnerName: category.winnerName,
      winnerImageUrl: category.winnerImageUrl,
      currentUserCorrect: currentVote
        ? category.winnerNomineeIds.includes(currentVote)
        : null,
      correctCount: correctParticipants.length,
      correctNames: correctParticipants
        .filter((participant) => participant.userId !== input.currentUserId)
        .map((participant) => names.get(participant.userId) ?? participant.userId),
      announcedCount: prefix.length,
      leaders,
      currentUser: currentEntry
        ? toPlayer(currentEntry, names, previousRanks)
        : null,
      leadChange,
      top,
    });

    previousRanks = new Map(ranked.map((entry) => [entry.userId, entry.rank]));
    if (leaderIds.length > 0) {
      previousLeaderIds = leaderIds;
    }
  }

  return activities.reverse();
}
